export enum FeedbackType {
  NEUTRAL = 'neutral',
  POSITIVE = 'positive',
  NEGATIVE = 'negative',
}

export enum FeedbackReason {
  WRONG_NUMBERS = 'wrong_numbers',
  WRONG_PERIOD_OR_FILTER = 'wrong_period_or_filter',
  MISUNDERSTOOD_QUESTION = 'misunderstood_question',
  MISSING_DATA_OR_CHART = 'missing_data_or_chart',
  TOO_LONG_OR_CONFUSING = 'too_long_or_confusing',
  TOO_SLOW_OR_ERROR = 'too_slow_or_error',
  OTHER = 'other',
  /** Never sent by the platform; kept because the backend enum and old items carry it. */
  REPORT_CONTENT = 'report_content',
  ACCURATE_DATA = 'accurate_data',
  CLEAR_ANSWER = 'clear_answer',
  SAVED_TIME = 'saved_time',
  GOOD_INSIGHT = 'good_insight',
}

/** Implicit behaviour signals counted per interaction (D8). */
export enum FeedbackSignal {
  COPY = 'copy',
  DOWNLOAD = 'download',
  REGENERATE = 'regenerate',
  /** Never sent by the platform: the detection heuristic was dropped. Mirrors the backend enum. */
  REPHRASE = 'rephrase',
}

export interface FeedbackReasonChip {
  reason: FeedbackReason;
  labelKey: string;
}

/** The chips the modal offers after a thumbs down, in display order. */
export const FEEDBACK_NEGATIVE_REASON_CHIPS: ReadonlyArray<FeedbackReasonChip> = [
  { reason: FeedbackReason.WRONG_NUMBERS, labelKey: 'Agent.FeedbackReasonWrongNumbers' },
  { reason: FeedbackReason.WRONG_PERIOD_OR_FILTER, labelKey: 'Agent.FeedbackReasonWrongPeriodOrFilter' },
  { reason: FeedbackReason.MISUNDERSTOOD_QUESTION, labelKey: 'Agent.FeedbackReasonMisunderstoodQuestion' },
  { reason: FeedbackReason.MISSING_DATA_OR_CHART, labelKey: 'Agent.FeedbackReasonMissingDataOrChart' },
  { reason: FeedbackReason.TOO_LONG_OR_CONFUSING, labelKey: 'Agent.FeedbackReasonTooLongOrConfusing' },
  { reason: FeedbackReason.TOO_SLOW_OR_ERROR, labelKey: 'Agent.FeedbackReasonTooSlowOrError' },
  { reason: FeedbackReason.OTHER, labelKey: 'Agent.FeedbackReasonOther' },
];

/** The chips the modal offers after a thumbs up, in display order. */
export const FEEDBACK_POSITIVE_REASON_CHIPS: ReadonlyArray<FeedbackReasonChip> = [
  { reason: FeedbackReason.ACCURATE_DATA, labelKey: 'Agent.FeedbackReasonAccurateData' },
  { reason: FeedbackReason.CLEAR_ANSWER, labelKey: 'Agent.FeedbackReasonClearAnswer' },
  { reason: FeedbackReason.SAVED_TIME, labelKey: 'Agent.FeedbackReasonSavedTime' },
  { reason: FeedbackReason.GOOD_INSIGHT, labelKey: 'Agent.FeedbackReasonGoodInsight' },
  { reason: FeedbackReason.OTHER, labelKey: 'Agent.FeedbackReasonOther' },
];

/** 200 body of PATCH /agents/conversations/{id}/feedback. */
export interface FeedbackResponse {
  conversation_id: string;
  timestamp: string;
  feedback: FeedbackType;
  feedback_reason: FeedbackReason | null;
  feedback_comment: string | null;
  feedback_updated_at: string | null;
  signals: Record<string, number>;
}

/** Request body of PATCH /agents/conversations/{id}/feedback. Phase 4 adds `signal`. */
export interface UpdateFeedbackPayload {
  timestamp: string;
  feedback?: FeedbackType;
  feedback_reason?: FeedbackReason;
  feedback_comment?: string;
  /** Mutually exclusive with `feedback`: the backend rejects both together. */
  signal?: FeedbackSignal;
}

export interface AgentUsedInfo {
  endpoint_name: string;
  retailer_name: string;
}

export interface ConversationTimestamps {
  created_at: string;
  updated_at: string;
  last_interaction_at: string;
}

export interface AgentConversation {
  conversation_id: string;
  title: string | null;
  client_name: string;
  user_email: string;
  agents_used: AgentUsedInfo[];
  timestamps: ConversationTimestamps;
}

export interface AgentConversationsResponse {
  conversations: AgentConversation[];
  total: number;
}

export interface InteractionAgentMetadata {
  endpoint_name: string;
  retailer_name: string;
  model: string;
}

export interface InteractionContent {
  input: string;
  output: string;
}

export interface GeneratedFile {
  file_name: string;
  file_size: number;
  file_type: string;
}

export interface FileDownloadUrlResponse {
  download_url: string;
  file_name: string;
  file_size: number;
  file_type: string;
  expires_in: number;
}

export interface AgentInteractionOption {
  id: string;
  label: string;
  hint?: string;
  /** Only on `<bops:choicegrid>` rows: the answers offered for that row. */
  choices?: { id: string; label: string }[];
}

export interface AgentInteraction {
  conversation_id: string;
  interaction_id: string;
  content: InteractionContent;
  agent_metadata: InteractionAgentMetadata;
  feedback: FeedbackType;
  feedback_reason?: FeedbackReason | null;
  feedback_comment?: string | null;
  feedback_updated_at?: string | null;
  timestamp: string;
  generated_files?: GeneratedFile[];
  /**
   * Interactive options the agent emitted for this interaction. Persisted on
   * the interaction record in DDB and re-emitted via the `options` field of
   * the `agent-conversation-interactions` response, so they survive refreshes.
   */
  options?: AgentInteractionOption[];
  /**
   * Multi-select interactive items the agent emitted (via `<bops:multiselect>`).
   * Persisted separately from `options` so the frontend renders checkboxes with
   * a confirm button; survives refreshes like `options`.
   */
  multiselect_options?: AgentInteractionOption[];
  /**
   * Choice-grid rows the agent emitted (via `<bops:choicegrid>`): the same
   * question asked of several subjects, each row carrying its own `choices`.
   * Persisted separately so a reload can rebuild the grid and the answers.
   */
  choicegrid_options?: AgentInteractionOption[];
}

export interface AgentConversationInteractionsResponse {
  conversation_id: string;
  interactions: AgentInteraction[];
}
